import { ChargingStatus } from '@/enums/ChargingStatus'
import { ConnectorDynamicsCpmStatus } from '@/enums/Connector/ConnectorDynamicsCpmStatus'
import type { IConnector } from '@/models/Chargers/Connector/IConnector'
import type { IElementEvseIdConnector } from '@/models/Chargers/Connector/IElementEvseIdConnector'
import type { IFlatConnector } from '@/models/Connectors/IFlatConnector'
import type { IEvseStatus } from '@/models/EvsesStatus/IEvseStatus'
import type { IEvseStatusFiltered } from '@/models/EvsesStatus/IEvseStatusFiltered'
import type { FilterCondition } from '@/models/Filtering/FilterCondition'
import type { ICharger } from '@/models/ICharger'
import type { IChargingSite } from '@/models/IChargingSite'
import type { IEvse } from '@/models/IEvse'
import type { ISession } from '@/models/Sessions/ISession'
import type { IStateItemConnector } from '@/models/StateItem/IStateItemConnector'
import i18n from '@/plugins/I18n'
import type { DataTableFilterMetaData } from 'primevue/datatable'
import { getIcon } from './AssetHelper'
import { extractValues } from './FilteringHelper'

const t = i18n.global.t

export function getConnectorList(charger: ICharger | null | undefined): IConnector[] {
  const result: IConnector[] = []
  if (charger) {
    charger.Evses?.forEach(e => e.Connectors?.forEach(c => result.push(c)))
  }
  return result
}

export function getConnectorsList(chargers: ICharger[]): IConnector[] {
  const result: IConnector[] = []
  if (chargers) {
    chargers.forEach(ch => ch.Evses?.forEach(e => e.Connectors?.forEach(c => result.push(c))))
  }
  return result
}

export function getConnectorFromSiteById(site: IChargingSite, connectorId: string): IConnector | null {
  if (site.Chargers) {
    const connectors = getConnectorsList(site.Chargers)
    return connectors.find(c => c.Id === connectorId) ?? null
  }
  return null
}

export function getConnectorFromSitesById(sites: IChargingSite[], connectorId: string): IConnector | null {
  if (!sites || sites.length === 0) {
    return null
  }

  for (const site of sites) {
    const connector = getConnectorFromSiteById(site, connectorId)
    if (connector) {
      return connector
    }
  }

  return null
}

export function getConnectorFromChargersById(chargers: ICharger[], connectorId: string): IConnector | null {
  if (!chargers || chargers.length === 0) {
    return null
  }
  return getConnectorsList(chargers).find(c => c.Id === connectorId) ?? null
}

export function getConnectorStatusLabel(status: ChargingStatus | undefined): string {
  const statusLabelMap = {
    [ChargingStatus.Available]: 'available',
    [ChargingStatus.Active]: 'active',
    [ChargingStatus.Preparing]: 'preparing',
    [ChargingStatus.Charging]: 'charging',
    [ChargingStatus.Reserved]: 'reserved',
    [ChargingStatus.SuspendedEV]: 'suspendedev',
    [ChargingStatus.Unavailable]: 'unavailable',
    [ChargingStatus.Unknown]: 'unknown',
    [ChargingStatus.Faulted]: 'faulted',
    [ChargingStatus.Finishing]: 'finishing',
    [ChargingStatus.SuspendedEVSE]: 'suspendedevse',
    [ChargingStatus.Occupied]: 'occupied'
  }
  return status !== undefined && statusLabelMap[status] !== undefined ? statusLabelMap[status] : 'unknown'
}

export function getConnectorStatusIcon(status: ChargingStatus | undefined) {
  const statusImageMap: Record<ChargingStatus, string> = {
    [ChargingStatus.Available]: getIcon('greenCheck'),
    [ChargingStatus.Active]: getIcon('greenCheck'),
    [ChargingStatus.Preparing]: getIcon('yellowEllipsis'),
    [ChargingStatus.Charging]: getIcon('yellowBolt'),
    [ChargingStatus.Unavailable]: getIcon('stoneCrossCircle'),
    [ChargingStatus.Unknown]: getIcon('slateQuestionCircle'),
    [ChargingStatus.Faulted]: getIcon('redMinusCircle'),
    [ChargingStatus.Finishing]: getIcon('yellowHourglass'),
    [ChargingStatus.SuspendedEV]: getIcon('yellowPause'),
    [ChargingStatus.SuspendedEVSE]: getIcon('yellowPauseCircle'),
    [ChargingStatus.Reserved]: getIcon('blueLock'),
    [ChargingStatus.Occupied]: getIcon('yellowCar')
  }
  return status !== undefined && statusImageMap[status] !== undefined ? statusImageMap[status] : getIcon('slateQuestionCircle')
}

export function getConnectorStatusImage(connector: IConnector) {
  if (connector.LifeCycleStatus === ConnectorDynamicsCpmStatus.Blocked) {
    return getIcon('redBlocked')
  }
  if (connector.LifeCycleStatus === ConnectorDynamicsCpmStatus.OutOfOrder) {
    return getIcon('redOutOfOrder')
  }
  if (connector.LifeCycleStatus === ConnectorDynamicsCpmStatus.Removed) {
    return getIcon('redRemoved')
  }
  return connector.ManuallyFaulted ? getConnectorStatusIcon(ChargingStatus.Faulted) : getConnectorStatusIcon(connector.ChargingStatus)
}

export function getConnectorLabel(connector: IConnector) {
  if (connector.LifeCycleStatus === ConnectorDynamicsCpmStatus.Blocked) {
    return t(`connectorChargingStatuses.blocked`)
  }
  if (connector.LifeCycleStatus === ConnectorDynamicsCpmStatus.OutOfOrder) {
    return t(`connectorChargingStatuses.outOfOrder`)
  }
  if (connector.LifeCycleStatus === ConnectorDynamicsCpmStatus.Removed) {
    return t(`connectorChargingStatuses.removed`)
  }
  if (connector.ManuallyFaulted) {
    return t(`connectorChargingStatuses.manuallyFaulted`)
  }
  return t(`connectorChargingStatuses.${getConnectorStatusLabel(connector.ChargingStatus)}`)
}

export const statusesOptions = [
  {
    name: t('siteMonitoring.chargingStatuses.available'),
    value: ChargingStatus.Available
  },
  {
    name: t('siteMonitoring.chargingStatuses.faulted'),
    value: ChargingStatus.Faulted
  },
  {
    name: t('siteMonitoring.chargingStatuses.finishing'),
    value: ChargingStatus.Finishing
  },
  {
    name: t('siteMonitoring.chargingStatuses.occupied'),
    value: ChargingStatus.Occupied
  },
  {
    name: t('siteMonitoring.chargingStatuses.preparing'),
    value: ChargingStatus.Preparing
  },
  {
    name: t('siteMonitoring.chargingStatuses.reserved'),
    value: ChargingStatus.Reserved
  },
  {
    name: t('siteMonitoring.chargingStatuses.suspendedEv'),
    value: ChargingStatus.SuspendedEV
  },
  {
    name: t('siteMonitoring.chargingStatuses.suspendedEvse'),
    value: ChargingStatus.SuspendedEVSE
  },
  {
    name: t('siteMonitoring.chargingStatuses.unavailable'),
    value: ChargingStatus.Unavailable
  },
  {
    name: t('siteMonitoring.chargingStatuses.unknown'),
    value: ChargingStatus.Unknown
  }
]

export function buildSitesConnectorStatusCustomCondition(connectorStatusFilterCondition: FilterCondition, statusMetaData: DataTableFilterMetaData): FilterCondition {
  connectorStatusFilterCondition.predicate = ''
  const statusValues = extractValues(statusMetaData)
  if (statusValues.length > 0) {
    connectorStatusFilterCondition.predicate = `
      Chargers != null &&
      Chargers.Any(charger =>
        charger.Evses != null &&
        charger.Evses.Any(evse =>
          evse.Connectors != null &&
          evse.Connectors.Any(connector =>
            connector.ChargingStatus in (${statusValues})
          )
        )
      )
    `
  }
  return connectorStatusFilterCondition
}

export function buildLCConnectorStatusCustomCondition(connectorStatusFilterCondition: FilterCondition, statusMetaData: DataTableFilterMetaData): FilterCondition {
  connectorStatusFilterCondition.predicate = ''
  const statusValues = extractValues(statusMetaData)
  if (statusValues.length > 0) {
    connectorStatusFilterCondition.predicate = `
      ChargingSite != null && ChargingSite.Chargers != null &&
      ChargingSite.Chargers.Any(charger =>
        charger.Evses != null &&
        charger.Evses.Any(evse =>
          evse.Connectors != null &&
          evse.Connectors.Any(connector =>
            connector.ChargingStatus in (${statusValues})
          )
        )
      )
    `
  }
  return connectorStatusFilterCondition
}

export function convertConnectorToFlatConnectors(evses: IEvse[] | null | undefined): IFlatConnector[] {
  return (
    evses?.flatMap(
      evse =>
        evse.Connectors?.map(connector => ({
          Id: connector.Id,
          EvseElementId: evse.ElementId,
          ConnectorElementId: connector.ElementId,
          ConnectorVisualId: Number.parseInt(connector.ElementId, 10),
          ManuallyFaulted: connector.ManuallyFaulted ?? false,
          ChargingStatus: connector.ChargingStatus,
          ConnectorModel: connector.ConnectorModel,
          LifeCycleStatus: getLifeCycleStatus(connector)
        })) ?? []
    ) ?? []
  )
}

export function getLifeCycleStatus(connector: IConnector): ConnectorDynamicsCpmStatus {
  if (connector.LifeCycleStatus) {
    return connector.LifeCycleStatus
  }

  if (connector.ManuallyFaulted) {
    return ConnectorDynamicsCpmStatus.OutOfOrder
  }

  return ConnectorDynamicsCpmStatus.Available
}

export function buildConnectorId(chargePointId: string, connector: IFlatConnector) {
  return `${chargePointId}-${connector.EvseElementId}-${connector.ConnectorElementId}`
}

export function buildChargersConnectorStatusCustomCondition(connectorStatusFilterCondition: FilterCondition, statusMetaData: DataTableFilterMetaData): FilterCondition {
  connectorStatusFilterCondition.predicate = ''
  const statusValue = extractValues(statusMetaData)
  if (statusValue !== undefined && statusValue !== null) {
    connectorStatusFilterCondition.predicate = `
      Evses != null &&
      Evses.Any(evse =>
        evse.Connectors != null &&
        evse.Connectors.Any(connector =>
          connector.ChargingStatus in (${statusValue})
        )
      )
    `
  }
  return connectorStatusFilterCondition
}

export const stateItemsConnectors: IStateItemConnector[] = [
  { state: t('siteMonitoring.chargingStatuses.available'), value: ChargingStatus.Available, label: 'available' },
  { state: t('siteMonitoring.chargingStatuses.reserved'), value: ChargingStatus.Reserved, label: 'reserved' },
  { state: t('siteMonitoring.chargingStatuses.occupied'), value: ChargingStatus.Occupied, label: 'occupied' },
  { state: t('siteMonitoring.chargingStatuses.finishing'), value: ChargingStatus.Finishing, label: 'finishing' },
  { state: t('siteMonitoring.chargingStatuses.preparing'), value: ChargingStatus.Preparing, label: 'preparing' },
  { state: t('siteMonitoring.chargingStatuses.suspendedEv'), value: ChargingStatus.SuspendedEV, label: 'suspendedev' },
  { state: t('siteMonitoring.chargingStatuses.suspendedEvse'), value: ChargingStatus.SuspendedEVSE, label: 'suspendedevse' },
  { state: t('siteMonitoring.chargingStatuses.faulted'), value: ChargingStatus.Faulted, label: 'faulted' },
  { state: t('siteMonitoring.chargingStatuses.offline'), value: ChargingStatus.Unavailable, label: 'offline' },
  { state: t('siteMonitoring.chargingStatuses.unknown'), value: ChargingStatus.Unknown, label: 'unknown' }
]

export function mapAndFilterEvseConnectors(evses: IEvseStatus[], isCollapsed: boolean, maxConnectors = 5): IEvseStatusFiltered[] {
  let totalConnectors = 0

  return (
    evses
      ?.map(evse => {
        let connectors = (evse?.Evse.Connectors ?? []).sort((a, b) => {
          const elementIdA = Number.parseInt(a.ElementId) || 0
          const elementIdB = Number.parseInt(b.ElementId) || 0
          return elementIdA - elementIdB
        })

        if (isCollapsed) {
          connectors = connectors.slice(0, Math.max(0, maxConnectors - totalConnectors))
          totalConnectors += connectors.length
        }

        return { EvseStatus: evse, Connectors: connectors }
      })
      .filter(evse => evse.Connectors.length > 0) ?? []
  )
}

// Allowed statuses for starting a session
export const startSessionAllowedStatuses = new Set([ChargingStatus.Available, ChargingStatus.Preparing, ChargingStatus.Reserved, ChargingStatus.Active, ChargingStatus.Occupied])

// Blocked statuses for unlocking a connector
export const unlockBlockedStatuses = new Set([ChargingStatus.Unavailable, ChargingStatus.Available, ChargingStatus.Active])

export const findSessionForConnector = (connector: IElementEvseIdConnector, activeSessions: ISession[] | null): ISession | undefined => {
  const connectorId = Number.parseInt(connector.ElementId)
  const evseIdParsed = connector.EvseElementId ? Number.parseInt(connector.EvseElementId) : undefined
  return activeSessions?.find(s => s.ConnectorId === connectorId && (s.EvseId === null || evseIdParsed === undefined || s.EvseId === evseIdParsed))
}
